import { useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSinal, useTempoRealConectado } from '../../nucleo/tempo-real';
import { buscarPaginas, buscarTudo, CHAVE } from '../../componentes/agendamento-comum';

export { buscarPaginas, buscarTudo, CHAVE, diaLocal, useDisponibilidade, useRecursos, useServicos } from '../../componentes/agendamento-comum';
import type { Agendamento, Bloqueio } from './tipos';

/** Páginas de agendamentos por vez na visão: 10 de 200 (2.000). Acima disso a tela avisa e oferece carregar mais (r9 robustez B3). */
export const PAGINAS_AGENDA = 10;

/** Sem a conexão ao vivo (Redis fora, rede), a agenda volta a se atualizar por consulta. */
const SEM_CONEXAO_MS = 60_000;

/**
 * Agendamentos do período em lotes de `PAGINAS_AGENDA` páginas de 200 (r10 robustez B1): "Carregar mais" busca SÓ o lote
 * seguinte, a partir do cursor onde o anterior parou, e acumula (antes a chave mudava e tudo era baixado de novo desde a página
 * 1). `itens` junta os lotes; `parcial` quando o servidor ainda tem mais.
 */
export function useAgendamentos(de: Date, ate: Date, recursoId: string, ativo = true) {
  const params = { de: de.toISOString(), ate: ate.toISOString(), recurso_id: recursoId || undefined };
  const aoVivo = useTempoRealConectado();
  const q = useInfiniteQuery({
    enabled: ativo,
    refetchInterval: aoVivo ? false : SEM_CONEXAO_MS,
    queryKey: [CHAVE, 'agendamentos', params.de, params.ate, recursoId],
    queryFn: ({ pageParam }) => buscarPaginas<Agendamento>('/agenda/agendamentos', params, PAGINAS_AGENDA, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (lote) => (lote.parcial ? lote.cursor : null),
  });
  const itens = useMemo(() => q.data?.pages.flatMap((p) => p.itens) ?? [], [q.data]);
  return { ...q, itens, parcial: !!q.hasNextPage, carregandoMais: q.isFetchingNextPage };
}

/**
 * Ficha 2026-11-03: a sincronia com o Google avisa que algo mudou e a agenda aberta recarrega sozinha. Vários sinais em seguida (uma
 * rodada por pessoa) viram uma recarga só.
 */
export function useAgendaAoVivo() {
  const qc = useQueryClient();
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  useSinal('agenda', () => {
    if (espera.current) return;
    espera.current = setTimeout(() => {
      espera.current = null;
      void qc.invalidateQueries({ queryKey: [CHAVE] });
      void qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
    }, 500);
  });
}

export function useBloqueios(de: Date, ate: Date, recursoId: string, ativo = true) {
  const aoVivo = useTempoRealConectado();
  return useQuery({
    enabled: ativo,
    refetchInterval: aoVivo ? false : SEM_CONEXAO_MS,
    queryKey: [CHAVE, 'bloqueios', de.toISOString(), ate.toISOString(), recursoId],
    queryFn: () => buscarTudo<Bloqueio>('/agenda/bloqueios', { de: de.toISOString(), ate: ate.toISOString(), recurso_id: recursoId || undefined }),
  });
}

export function deDiaLocal(dia: string): Date {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number];
  return new Date(a, m - 1, d);
}
